import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { Env } from 'types/env/Env.type';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { stateService } from 'stores/state-service';
import { validateEnv } from 'utils/env/validate-env';
import axios from 'axios';
import DOMPurify from 'dompurify';

const EnvContext = createContext<(Env & { setConfig: (env: Env) => void }) | undefined>(undefined);
EnvContext.displayName = 'Environment Variables Configuration';

const setTitle = (title: string) => {
  document.title = title;
};

const getEnvUrl = (baseUrl: string, routerBasePath?: string) => {
  const resolvedRouterPath = routerBasePath ?? '';
  const newUrl = baseUrl + resolvedRouterPath + 'config/__ENV.json';
  const formattedUrl = newUrl.replaceAll('//', '/');
  return formattedUrl;
};

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [doneLoadingConfig, setDoneLoadingConfig] = useState(false);
  const [config, setConfig] = useState({
    apiUrl: import.meta.env.VITE_API_URL,
    routerBasePath: import.meta.env.VITE_ROUTER_BASE_PATH,
    applicationName: import.meta.env.VITE_APPLICATION_NAME,
    versionId:
      import.meta.env.MODE === 'development' ? 'LOCAL_BUILD' : import.meta.env.VITE_VERSION_ID,
    theme: 'glacier'
  } as Env);

  useEffect(() => {
    axios
      .get(getEnvUrl(import.meta.env.BASE_URL))
      .then((response) => {
        const validatedConfig = validateEnv(response.data, config);
        setConfig(validatedConfig);
        stateService.setStore(validatedConfig);
        const sanitizedApplicationName = DOMPurify.sanitize(validatedConfig.applicationName);
        setTitle(sanitizedApplicationName);
        setDoneLoadingConfig(true);
        logger.setConfig({
          logLevel: validatedConfig.logLevel,
          shouldTrace: import.meta.env.MODE === 'development'
        });
      })
      .catch((err) => {
        throw new Error('Failed to fetch environment config file.', err);
      });
  }, []);

  return (
    <EnvContext.Provider value={{ ...config, setConfig }}>
      {doneLoadingConfig && children}
    </EnvContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(EnvContext);

  if (!context) {
    throw new Error('Cannot use config outside of a Config Context Provider');
  }

  return context;
};
